import type { Exercise, Grade } from "@repo/hindi-shared";
import { letterById, wordById } from "@repo/hindi-shared";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import type { ReactElement } from "react";

import { Barakhadi } from "../exercises/Barakhadi";
import { LetterIntro } from "../exercises/LetterIntro";
import { PaperDictation } from "../exercises/PaperDictation";
import { SentenceReading } from "../exercises/SentenceReading";
import { SoundToSymbol } from "../exercises/SoundToSymbol";
import { SymbolToSound } from "../exercises/SymbolToSound";
import { WordBuilder } from "../exercises/WordBuilder";
import { WordReading } from "../exercises/WordReading";
import type { ExerciseProps, PaperDictationProps } from "../exercises/types";
import { DualLabel } from "../ui/DualLabel";
import { InkButton } from "../ui/InkButton";

/** Exercise kinds with a real, purpose-built component. `paper-dictation` is
 * handled separately by {@link PaperBlockHost} — the whole "paper" block is
 * one `PaperDictation` mount, not one component per exercise. */
const IMPLEMENTED_KINDS = new Set<Exercise["kind"]>([
  "letter-intro",
  "sound-to-symbol",
  "symbol-to-sound",
  "barakhadi",
  "word-reading",
  "word-builder",
  "sentence-reading",
]);

type ExerciseComponent = (props: ExerciseProps) => ReactElement | null;

const KIND_COMPONENTS: Partial<Record<Exercise["kind"], ExerciseComponent>> = {
  "letter-intro": LetterIntro,
  "sound-to-symbol": SoundToSymbol,
  "symbol-to-sound": SymbolToSound,
  barakhadi: Barakhadi,
  "word-reading": WordReading,
  "word-builder": WordBuilder,
  "sentence-reading": SentenceReading,
};

/** Best-effort display text for an item id, for the "coming soon" fallback. */
function displayText(itemId: string): string {
  const letter = letterById.get(itemId);
  if (letter !== undefined) return letter.glyph;
  const word = wordById.get(itemId);
  if (word !== undefined) return word.devanagari;
  return itemId;
}

const FALLBACK_GRADE: Grade = 2;

/**
 * ComingSoon — defensive fallback for any exercise kind without a real
 * component. Every kind has a real implementation as of Task 18
 * (paper-dictation is special-cased entirely — see {@link PaperBlockHost} —
 * rather than routed through here), so this only fires if a future kind is
 * added to the union before its component lands. Shows the item's
 * glyph/word text and completes on an explicit click (not a timer) so the
 * learner keeps control of pacing; grades a flat "Good" (2) since no real
 * judgment happened.
 */
function ComingSoon({ exercise, onComplete }: ExerciseProps) {
  return (
    <div className="flex flex-col items-center gap-8 text-center">
      <p className="font-dev text-sm text-ink-soft uppercase">
        <DualLabel variant="inline" en="Coming soon" hi="जल्द आ रहा है" />
      </p>
      <p className="font-dev text-6xl text-ink">
        {displayText(exercise.itemId)}
      </p>
      <InkButton
        variant="ghost"
        onClick={() =>
          onComplete({ grade: FALLBACK_GRADE, itemId: exercise.itemId })
        }
      >
        <DualLabel variant="inline" en="Continue →" hi="आगे →" />
      </InkButton>
    </div>
  );
}

interface ExerciseHostProps extends ExerciseProps {
  /** Unique per exercise-in-plan key material (block/exercise index + itemId). */
  runnerKey: string;
}

/**
 * ExerciseHost — resolves `exercise.kind` to its real component, or the
 * `ComingSoon` fallback for kinds not yet implemented, and wraps it in a
 * framer-motion fade + 12px x-shift page-turn on block transitions.
 *
 * BINDING CONTRACT (Task 8 review): the caller MUST pass a `runnerKey` that
 * is unique per exercise instance (e.g. `${blockIndex}-${exerciseIndex}-${itemId}`)
 * as this component's React `key`. Exercise components speak on mount via a
 * `[]`-dep effect and rely on a full remount to speak again for the next
 * exercise — reusing the same component instance across exercises would
 * silently stop the audio cue from firing.
 */
export function ExerciseHost({
  exercise,
  audio,
  onComplete,
  runnerKey,
}: ExerciseHostProps) {
  const prefersReducedMotion = useReducedMotion();
  const Component = IMPLEMENTED_KINDS.has(exercise.kind)
    ? KIND_COMPONENTS[exercise.kind]!
    : ComingSoon;

  const animation = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, x: 12 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -12 },
        transition: { duration: 0.2 },
      };

  return (
    <AnimatePresence mode="wait">
      <motion.div key={runnerKey} {...animation}>
        <Component exercise={exercise} audio={audio} onComplete={onComplete} />
      </motion.div>
    </AnimatePresence>
  );
}

interface PaperBlockHostProps extends Omit<
  PaperDictationProps,
  "onItemComplete"
> {
  /** Unique per-block key material (block index), so a new "paper" block
   * (unlikely — there's at most one per plan — but defensive) remounts. */
  runnerKey: string;
  onItemComplete: PaperDictationProps["onItemComplete"];
}

/**
 * PaperBlockHost — mounts {@link PaperDictation} once for the entire
 * "paper" `SessionBlock`, unlike every other block kind which mounts one
 * component per exercise via {@link ExerciseHost}. `PaperDictation`
 * self-manages the intro card, per-item dictation, reveal, and self-mark
 * flow across every exercise in the block, calling `onItemComplete` once
 * per item — the caller (SessionScreen) drives the same
 * `complete-exercise` runner action per call, exactly as it does for every
 * other exercise kind.
 */
export function PaperBlockHost({
  exercises,
  audio,
  onItemComplete,
  alreadyCompleted,
  runnerKey,
}: PaperBlockHostProps) {
  const prefersReducedMotion = useReducedMotion();
  const animation = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, x: 12 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -12 },
        transition: { duration: 0.2 },
      };

  return (
    <AnimatePresence mode="wait">
      <motion.div key={runnerKey} {...animation}>
        <PaperDictation
          exercises={exercises}
          audio={audio}
          onItemComplete={onItemComplete}
          alreadyCompleted={alreadyCompleted}
        />
      </motion.div>
    </AnimatePresence>
  );
}
